import React, { useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import logo from '../../assets/images/logo.jpeg';

/**
 * A reusable loading component that displays the Audit-AI logo with a spinning animation
 * 
 * @param {Object} props - Component props
 * @param {number} [props.size=50] - Size of the loader in pixels
 * @param {string} [props.message=null] - Optional message to display below the loader
 * @param {string} [props.color='#0D6EFD'] - Color of the spinning border
 * @param {number} [props.duration=1.5] - Animation duration in seconds
 * @param {number} [props.opacity=0.7] - Opacity of the spinning border
 * @returns {JSX.Element} The LogoLoader component
 */
const LogoLoader = ({ 
    size = 50, 
    message = null,
    color = '#0D6EFD',
    duration = 1.5,
    opacity = 0.7
}) => {
    const [imgError, setImgError] = useState(false);
    
    // Define the spin animation
    const spin = keyframes`
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    `;

    // Handle image load error
    const handleError = () => {
        console.error('Failed to load logo image');
        setImgError(true);
    };

    // Log when component renders
    console.log('LogoLoader rendering, size:', size);

    return (
        <Box 
            sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2
            }}
        >
            <Box 
                sx={{ 
                    position: 'relative',
                    width: size,
                    height: size,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {/* Logo with static appearance or fallback */}
                {imgError ? (
                    <CircularProgress 
                        size={size * 0.7} 
                        thickness={4}
                        sx={{ color: color, zIndex: 2 }}
                    />
                ) : (
                    <img 
                        src={logo} 
                        alt="Audit-AI Logo" 
                        onError={handleError}
                        style={{ 
                            width: size * 0.7, 
                            height: size * 0.7,
                            borderRadius: '50%',
                            objectFit: 'contain',
                            position: 'relative',
                            zIndex: 2
                        }} 
                    />
                )}
                
                {/* Spinning border around the logo */}
                <Box 
                    sx={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        border: '2px solid transparent',
                        borderTopColor: color,
                        borderRightColor: color,
                        opacity: opacity,
                        animation: `${spin} ${duration}s linear infinite`,
                        zIndex: 1
                    }}
                />
            </Box>
            
            {message && (
                <Typography variant="body2" color="text.secondary">
                    {message}
                </Typography>
            )}
        </Box>
    );
};

export default LogoLoader; 